import { useEffect, useRef, useState } from 'react';

import { Center, Loader, Text } from '@mantine/core';

import { isAxiosError } from 'axios';

import { vnextApiClient } from '@/services/vnext-api';
import classes from './PdfViewer.module.css';

interface PdfViewerProps {
  /** Renders the report's PDF derivative (fetched via download-pdf). */
  reportId?: string;
  /** OR: a direct (presigned) URL of a PDF — used by the Documents-tab
   * file previews. Takes precedence over reportId. */
  pdfUrl?: string;
  /**
   * Called when the server reports the PDF cannot be produced (conversion
   * failed or the converter is not configured) — the parent hides the
   * Presentation tab and degrades to the Content view.
   */
  onUnavailable?: () => void;
}

/** Statuses that mean "no PDF for this report", as opposed to a transient fault. */
const UNAVAILABLE_STATUSES = new Set([409, 502, 503]);

/**
 * Presentation-layer report view: renders the server-converted PDF derivative
 * of the report docx with pdf.js — one canvas per page plus a selectable text
 * layer, in the same page-card styling as the Content view.
 */
export function PdfViewer({ reportId, pdfUrl, onUnavailable }: Readonly<PdfViewerProps>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let destroyDocument: (() => void) | undefined;

    async function load() {
      try {
        // pdf.js is heavy and only needed on this tab — load it on demand.
        const pdfjs = await import('pdfjs-dist');
        pdfjs.GlobalWorkerOptions.workerSrc = new URL(
          'pdfjs-dist/build/pdf.worker.min.mjs',
          import.meta.url
        ).toString();

        const url =
          pdfUrl ?? (reportId ? (await vnextApiClient.downloadReportPdf(reportId)).url : null);
        if (!url) {
          throw new Error('PdfViewer needs a reportId or a pdfUrl');
        }
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch PDF (${response.status})`);
        }
        const data = await response.arrayBuffer();
        if (cancelled || !containerRef.current) {
          return;
        }

        const loadingTask = pdfjs.getDocument({ data });
        destroyDocument = () => void loadingTask.destroy();
        const doc = await loadingTask.promise;
        const container = containerRef.current;
        const containerWidth = container.clientWidth || 900;
        const devicePixelRatio = window.devicePixelRatio || 1;

        for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber++) {
          const page = await doc.getPage(pageNumber);
          if (cancelled || !containerRef.current) {
            return;
          }
          // Scale each page to the container width; canvases render at
          // devicePixelRatio for sharpness and are CSS-sized back down.
          const scale = containerWidth / page.getViewport({ scale: 1 }).width;
          const viewport = page.getViewport({ scale });

          const pageDiv = document.createElement('div');
          pageDiv.className = classes.page;
          pageDiv.style.width = `${viewport.width}px`;
          pageDiv.style.height = `${viewport.height}px`;
          // pdf.js's TextLayer positions its spans relative to this variable.
          pageDiv.style.setProperty('--scale-factor', String(scale));

          const canvas = document.createElement('canvas');
          canvas.width = Math.floor(viewport.width * devicePixelRatio);
          canvas.height = Math.floor(viewport.height * devicePixelRatio);
          canvas.className = classes.pageCanvas;
          pageDiv.appendChild(canvas);

          const context = canvas.getContext('2d');
          if (context) {
            await page.render({
              canvasContext: context,
              canvas,
              viewport,
              transform:
                devicePixelRatio === 1
                  ? undefined
                  : [devicePixelRatio, 0, 0, devicePixelRatio, 0, 0],
            }).promise;
          }

          const textLayerDiv = document.createElement('div');
          textLayerDiv.className = classes.textLayer;
          pageDiv.appendChild(textLayerDiv);
          await new pdfjs.TextLayer({
            textContentSource: page.streamTextContent(),
            container: textLayerDiv,
            viewport,
          }).render();

          if (cancelled || !containerRef.current) {
            return;
          }
          containerRef.current.appendChild(pageDiv);
        }
      } catch (err) {
        if (cancelled) {
          return;
        }
        if (isAxiosError(err) && UNAVAILABLE_STATUSES.has(err.response?.status ?? 0)) {
          setError('A PDF rendering of this report is not available.');
          onUnavailable?.();
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load PDF');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
      destroyDocument?.();
    };
    // onUnavailable is intentionally not a dependency: parents pass inline
    // callbacks and the load must run exactly once per source.
  }, [reportId, pdfUrl]);

  return (
    <>
      {isLoading && (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      )}
      {error && (
        <Center py="xl">
          <Text c="red" size="sm">
            {error}
          </Text>
        </Center>
      )}
      <div
        ref={containerRef}
        className={classes.pdfContent}
        style={{ display: isLoading ? 'none' : undefined }}
      />
    </>
  );
}
